import clsx from 'clsx';
import { useEffect, useState } from 'react';
import Enllac from 'components/corporatiu/Enllac';
import styles from './llistatcontingut.module.scss';
import { useViewport } from 'hooks/corporatiu/useViewport';
import Icona from 'components/corporatiu/ui/Icona';
import __isEmpty from 'lodash/isEmpty';

function LlistatContingut(props) {
    const {
        items: staticItems,
        titol,
        routeType,
        numColumnsRow,
        novaFinestra,
        tipus,
        url_dades,
        nodes,
        nodeTitol,
        seccio,
        estil,
        tema,
        tag = 'section'
    } = props;
    const TagSeo = tag;
    const [dynamicItems, setDynamicItems] = useState(null);
    const [dynamicTitol, setDynamicTitol] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { isOnlyMobile } = useViewport();

    const normalizeUrlDadesLang = (url) => {
        if (!url) return url;

        // Casos: menu_atencio_.json, menu_atencio_undefined.json, menu_atencio_null.json
        return url.replace(/_(?:undefined|null)\.json(\?.*)?$/i, '_ca.json$1').replace(/_\.json(\?.*)?$/i, '_ca.json$1');
    };

    useEffect(() => {
        if (url_dades) {
            const fetchData = async () => {
                setLoading(true);
                setError(null);

                try {
                    const finalUrl = normalizeUrlDadesLang(url_dades);
                    const response = await fetch(finalUrl);

                    if (!response.ok) {
                        throw new Error(`Error ${response.status}: ${response.statusText}`);
                    }

                    const data = await response.json();

                    let items;
                    let titol = [];

                    if (nodes) {
                        const nodesPath = nodes.split('.');
                        items = nodesPath.reduce((obj, key) => {
                            const result = obj?.[key];
                            return result;
                        }, data);
                    }
                    if (nodeTitol) {
                        const titolPath = nodeTitol.split('.');
                        titol = titolPath.reduce((obj, key) => {
                            const result = obj?.[key];
                            return result;
                        }, data);
                    }

                    setDynamicItems(items);
                    setDynamicTitol(titol[0]?.text || '');
                } catch (err) {
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };

            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url_dades, nodes]);

    const [open, setOpen] = useState(false);

    // Utilitza items dinàmics si estan disponibles, sinó els estàtics
    const items = dynamicItems || staticItems;
    const titolListat = dynamicTitol || titol;

    if (loading) {
        return <div>Carregant...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    const stylesLlistat = clsx({
        [styles.llistatContinguts]: true,
        [styles.botonera]: tipus === 'botonera',
        [styles.atencio]: tipus === 'atencio',
        [styles.menu]: tipus === 'menu',
        [styles.menuMobile]: tipus === 'menu' && isOnlyMobile,
        [styles.obert]: open,
        [styles[tema]]: !__isEmpty(tema),
        [styles[estil]]: !__isEmpty(estil),
        [styles[`col${numColumnsRow}`]]: numColumnsRow
    });

    const toggleAccordion = () => {
        setOpen(!open);
    };

    const hasHeader = items && Boolean(titolListat || items[0]?.header);
    const isMenuMobile = hasHeader && isOnlyMobile && tipus === 'menu';

    const renderHeaderContent = () => {
        if (!hasHeader) return null;

        if (items[0]?.header && items[0]?.url) {
            const isSelected = seccio !== undefined && items[0]?.seccioHeader === seccio;

            const stylesHeader = clsx({
                [styles.header]: true,
                [styles.seccioHeaderActiva]: isSelected
            });

            return (
                <div className={stylesHeader} data-test-id='header-llistat'>
                    <Enllac href={items[0]?.url} titol={items[0]?.header} novaFinestra={novaFinestra} className={styles.titol} />
                </div>
            );
        }

        if (isMenuMobile) {
            return (
                <button className={styles.header} aria-expanded={open} onClick={toggleAccordion}>
                    <div className={styles.titol}>{titol || items[0]?.header}</div>
                    <Icona
                        className={styles.iconaDesplegable}
                        icona='fletxa_simple_avall'
                        alt='fletxa desplegar'
                        isBlanc={false}
                    />
                </button>
            );
        }

        return (
            <div className={styles.header}>
                <div className={styles.titol} data-testid='header-llistat-titol'>
                    {titolListat || items[0]?.header}
                </div>
            </div>
        );
    };

    const headerContent = renderHeaderContent();

    return items && items.length > 0 ? (
        <TagSeo className={stylesLlistat}>
            {headerContent}
            <ul>
                {items.map((item, i) => {
                    const titleLink = item.desc || item.titol || item.titol_seccio;
                    const link = item.url || item.nom_bonic;
                    const routeItem = { slug: titleLink, id: item.id, type: routeType, href: link };
                    const isSelected = seccio !== undefined && (item?.seccio === seccio || String(item.id) === seccio);
                    const ariaCurrentItem = isSelected ? 'page' : undefined;

                    const stylesItemLlistat = clsx({
                        [styles.elementLlistat]: true,
                        [styles.seccioActiva]: isSelected,
                        [styles.separador]: item.separador === true
                    });

                    const itemKey = item.id ?? link ?? titleLink;

                    return (
                        titleLink && (
                            <li className={stylesItemLlistat} key={`element-llistat-${itemKey}`}>
                                {tipus === 'botonera' ? (
                                    <Enllac
                                        route={routeItem}
                                        className={styles.infoElement}
                                        novaFinestra={true}
                                        ariaCurrent={ariaCurrentItem}
                                    >
                                        <span>{titleLink}</span>
                                        <Icona
                                            className={styles.icona}
                                            icona='linkexterior'
                                            alt='obrir en finestra nova'
                                            path='corporatiu'
                                            aria-hidden='true'
                                            mida='16'
                                            inline={true}
                                            isBlanc={false}
                                        />
                                    </Enllac>
                                ) : (
                                    <Enllac
                                        route={routeItem}
                                        className={styles.infoElement}
                                        titol={titleLink}
                                        novaFinestra={novaFinestra}
                                        ariaCurrent={ariaCurrentItem}
                                        dataTestId={`link-llistat-${i}`}
                                    />
                                )}
                            </li>
                        )
                    );
                })}
            </ul>
        </TagSeo>
    ) : (
        <></>
    );
}

export default LlistatContingut;
